import React, {useRef, useEffect, useState} from 'react';
import { API } from "aws-amplify";
import { getDashboardExperienceEmbedURL, initializeDashboardEmbed, initializeQuickChatEmbed } from 'util/dashboard';
import { toast } from 'react-toastify';

export function DashboardsEmbedWrapper(props) {
    const quicksightRef = useRef();
    const quickChatRef = useRef();

    const [quickChatAvailable, setQuickChatAvailable] = useState(false);
    const [quickChatPanelVisible, setQuickChatPanelVisible] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function destroyExistingEmbeds() {
            if (quicksightRef.current && typeof quicksightRef.current.destroy === 'function') {
                quicksightRef.current.destroy();
            }

            if (quickChatRef.current && typeof quickChatRef.current.destroy === 'function') {
                quickChatRef.current.destroy();
            }

            quicksightRef.current = null;
            quickChatRef.current = null;

            const dashboardContainer = document.getElementById('embeddingContainer');
            if (dashboardContainer) {
                dashboardContainer.innerHTML = '';
            }

            const quickChatContainer = document.getElementById('quickChatContainer');
            if (quickChatContainer) {
                quickChatContainer.innerHTML = '';
            }
        }

        if (props.dashboardId) {
            async function init() {
                try {
                    await destroyExistingEmbeds();

                    setQuickChatAvailable(false);
                    setQuickChatPanelVisible(false);

                    /*
                     * Keep dashboard embed on existing DashboardApi path.
                     */
                    const EmbedUrl = await API.get('DashboardApi', `/dashboard/${props.dashboardId}/view`);

                    if (cancelled) {
                        return;
                    }

                    quicksightRef.current = await initializeDashboardEmbed(EmbedUrl.embedUrl, {
                        container: '#embeddingContainer',
                        height: props.dashboardHeight || '750px',
                        width: '100%',
                    });

                    if (!props.showQuickChat) {
                        return;
                    }

                    try {
                        /*
                         * Initialize Quick Chat only once.
                         *
                         * Pin the HSD Quick Chat experience to the configured
                         * Healthcare Claims Insights Agent so users cannot switch
                         * to My Assistant or another agent.
                         */
                        const dashboardIdMode = props.dashboardId.includes('dash_query_vis_') ? 'queryvis' : 'quicksight';

                        const quickChatEmbedUrl = await getDashboardExperienceEmbedURL(props.dashboardId, {
                            experience: 'quick-chat',
                            dashboardIdMode
                        });

                        if (cancelled) {
                            return;
                        }

                        const quickChatContainer = document.getElementById('quickChatContainer');
                        if (quickChatContainer) {
                            quickChatContainer.innerHTML = '';
                        }

                        quickChatRef.current = await initializeQuickChatEmbed(quickChatEmbedUrl, {
                            container: '#quickChatContainer',
                            height: props.quickChatHeight || '750px',
                            width: '100%',
                            fixedAgentId: props.quickChatFixedAgentId,
                            contentOptions: {
                                promptOptions: {
                                    /*
                                     * Keep file attachments and web search disabled while
                                     * the HSD experience is pinned to the configured agent.
                                     */
                                    allowFileAttachments: false,
                                    showAgentKnowledgeBoundary: true,
                                    showWebSearch: false,
                                    showChatHistory: true,
                                    showPromptArea: true,
                                    enablePrivateMode: true,
                                },
                                footerOptions: {
                                    showBrandAttribution: true,
                                    showUsagePolicy: true,
                                }
                            }
                        });

                        if (cancelled) {
                            return;
                        }

                        setQuickChatAvailable(true);
                        setQuickChatPanelVisible(true);
                    } catch (quickChatErr) {
                        if (cancelled) {
                            return;
                        }

                        setQuickChatAvailable(false);
                        setQuickChatPanelVisible(false);

                        if (quickChatErr?.response?.status === 403) {
                            return;
                        }

                        console.error('Quick Chat embed initialization failed:', quickChatErr);

                        toast(`Dashboard loaded, but Quick Chat could not be embedded: ${quickChatErr.message || 'Unknown error'}`, {
                            type: toast.TYPE.WARNING,
                            autoClose: 8000
                        });
                    }
                } catch (err) {
                    console.error('Dashboard embed initialization failed:', err);

                    if (cancelled) {
                        return;
                    }

                    setQuickChatAvailable(false);
                    setQuickChatPanelVisible(false);

                    toast(`Failed to load dashboard: ${err.message || 'Unknown error'}`, {
                        type: toast.TYPE.ERROR,
                        autoClose: 5000
                    });
                }
            }

            init();
        }

        return () => {
            cancelled = true;

            if (quicksightRef.current && typeof quicksightRef.current.destroy === 'function') {
                quicksightRef.current.destroy();
            }

            if (quickChatRef.current && typeof quickChatRef.current.destroy === 'function') {
                quickChatRef.current.destroy();
            }

            quicksightRef.current = null;
            quickChatRef.current = null;
        };
    }, [
        props.dashboardId,
        props.showQuickChat,
        props.quickChatFixedAgentId,
        props.dashboardHeight,
        props.quickChatHeight
    ]);

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            {props.showQuickChat && quickChatAvailable && (
                <button
                    type="button"
                    onClick={() => setQuickChatPanelVisible((visible) => !visible)}
                    aria-label={quickChatPanelVisible ? 'Collapse agent panel' : 'Expand agent panel'}
                    aria-expanded={quickChatPanelVisible}
                    aria-controls="quickChatContainer"
                    title={quickChatPanelVisible ? 'Collapse agent' : 'Expand agent'}
                    style={{
                        position: 'fixed',
                        top: '9.375rem',
                        right: '1.5rem',
                        zIndex: 1000,
                        width: '2.5rem',
                        height: '2.5rem',
                        borderRadius: '1.25rem',
                        border: '0.0625rem solid #d5d9d9',
                        background: '#ffffff',
                        cursor: 'pointer',
                        boxShadow: '0 0.0625rem 0.25rem rgba(0, 0, 0, 0.18)',
                        fontSize: quickChatPanelVisible ? '1.25rem' : '1.375rem',
                        lineHeight: '2.375rem',
                        textAlign: 'center',
                        padding: 0
                    }}
                >
                    {quickChatPanelVisible ? '×' : '✦'}
                </button>
            )}

            <div style={{ display: 'flex', gap: quickChatPanelVisible ? '1rem' : '0', width: '100%' }}>
                <div
                    style={{
                        flex: quickChatPanelVisible ? '0 0 65%' : '1 1 100%',
                        minWidth: 0,
                        transition: 'flex-basis 200ms ease'
                    }}
                >
                    <div
                        className="dashboard-graph-full"
                        id="embeddingContainer"
                        style={{ height: props.dashboardHeight || '750px' }}
                    ></div>
                </div>

                <div
                    aria-hidden={!quickChatPanelVisible}
                    style={{
                        flex: quickChatPanelVisible ? '0 0 35%' : '0 0 0',
                        minWidth: quickChatPanelVisible ? '22.5rem' : '0',
                        width: quickChatPanelVisible ? '35%' : '0',
                        overflow: 'hidden',
                        visibility: quickChatPanelVisible ? 'visible' : 'hidden',
                        pointerEvents: quickChatPanelVisible ? 'auto' : 'none',
                        transition: 'flex-basis 200ms ease, width 200ms ease'
                    }}
                >
                    {!quickChatAvailable && (
                        <div style={{ padding: '12px', fontSize: '14px' }}>
                            Loading Quick Chat...
                        </div>
                    )}

                    <div
                        id="quickChatContainer"
                        style={{
                            height: props.quickChatHeight || '750px',
                            minWidth: '22.5rem'
                        }}
                    ></div>
                </div>
            </div>
        </div>
    );
}
